import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, FileWarning } from 'lucide-react';

interface CancelOrderModalProps {
  isOpen: boolean;
  orderCode?: string;
  onClose: () => void;
  /** Caller may throw an error with `requiresForce: true` (set by the API
   *  layer when the backend returns 409 fiscal_document_issued) and
   *  `fiscalCanCancel` (the receipt has a MARK, so a cancellation document can
   *  be issued). The modal catches that, switches to the "fiscal active"
   *  warning state, and a second submit invokes onConfirm with `force=true`.
   *  On force the backend cancels the order AND its receipt automatically. */
  onConfirm: (reason: string, force?: boolean) => Promise<void>;
  /** 'store' (default): force cancels the order AND its receipt (ΑΑΔΕ
   *  cancellation). 'org': the HQ endpoint intentionally leaves the receipt
   *  ACTIVE on force — the warning text must say so, or the user is told the
   *  opposite of what will happen. */
  variant?: 'store' | 'org';
  /** Platform of the order being cancelled. For 'box' an info line explains
   *  that Box is NOT notified (no integrator cancel flow exists — confirmed by
   *  Desquared) and the platform must be contacted separately. */
  platformName?: string | null;
  /** Τραπέζι με απόδειξη/τιμολόγιο (25/10/2026): δικός του τίτλος, η προειδοποίηση για το ακυρωτικό ΠΡΙΝ το πάτημα,
   *  το κείμενο του κουμπιού και οι έτοιμες αιτίες «Ήθελε τιμολόγιο» / «Χρεώθηκε αλλού». */
  title?: string;
  notice?: React.ReactNode;
  confirmLabel?: string;
  extraPresetKeys?: string[];
  /** Δεν επιτρέπεται (π.χ. χωρίς δικαίωμα, παραστατικό χωρίς MARK): το μήνυμα στη θέση της προειδοποίησης, κουμπί κλειστό. */
  blockedMessage?: string | null;
}

const BASE_PRESET_KEYS = ['customerRequest', 'wrongItem', 'outOfStock', 'longWait', 'duplicate', 'paymentIssue'];

const CancelOrderModal: React.FC<CancelOrderModalProps> = ({
  isOpen,
  orderCode,
  onClose,
  onConfirm,
  variant = 'store',
  platformName = null,
  title,
  notice = null,
  confirmLabel,
  extraPresetKeys = [],
  blockedMessage = null,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the backend asks for explicit force confirmation (a fiscal doc
  // is already issued). The user can then submit again to cancel anyway.
  const [needsForceConfirm, setNeedsForceConfirm] = useState(false);
  // Whether the linked receipt has a MARK, so a cancellation document can be
  // issued to AADE. When true, forcing the cancel also cancels the receipt;
  // when false (no MARK) only the order is cancelled.
  const [fiscalCanCancel, setFiscalCanCancel] = useState(false);

  if (!isOpen) return null;

  const closeModal = () => {
    setNeedsForceConfirm(false);
    setFiscalCanCancel(false);
    setError(null);
    onClose();
  };

  const handleSubmit = async (force = false) => {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(reason.trim(), force);
      setReason('');
      setNeedsForceConfirm(false);
      setFiscalCanCancel(false);
      onClose();
    } catch (e: any) {
      if (e?.requiresForce && !force) {
        // Backend says the order has a successful fiscal document. Show the
        // warning and let the user confirm explicitly.
        setNeedsForceConfirm(true);
        setFiscalCanCancel(e?.fiscalCanCancel === true);
        setError(null);
      } else {
        setError(e?.message || t('cancelOrder.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cashier-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="cashier-dialog w-full max-w-md bg-surface-2 dark:bg-surface-1 border border-line rounded-2xl shadow-xl">
        <div className="cashier-dialog-header flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <h3 className="font-semibold">{title || t('cancelOrder.title')}</h3>
          </div>
          <button onClick={closeModal} className="text-t3 hover:text-t1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-t2">
            {orderCode
              ? t('cancelOrder.confirmWithCode', { code: orderCode })
              : t('cancelOrder.confirm')}
          </p>

          {(blockedMessage || notice) && (
            <div
              data-testid="cancel-fiscal-notice"
              className="flex gap-3 p-3 rounded-lg bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200"
            >
              <FileWarning className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm space-y-2">{blockedMessage || notice}</div>
            </div>
          )}

          {/* Fiscal-active warning — appears when the backend returns 409
              fiscal_document_issued. Confirming cancels the order AND its
              receipt (a cancellation document is issued to AADE). Receipts
              without a MARK cannot be cancelled — only the order is. */}
          {needsForceConfirm && (
            <div className="flex gap-3 p-3 rounded-lg bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200">
              <FileWarning className="w-5 h-5 shrink-0 mt-0.5" />
              <div className="text-sm space-y-2">
                <div className="font-semibold">{t('cancelOrder.fiscalActiveTitle', { defaultValue: 'Έχει εκδοθεί παραστατικό' })}</div>
                {variant === 'org' ? (
                  <div className="text-amber-900/90 dark:text-amber-200/90">
                    {/* The HQ endpoint deliberately KEEPS the receipt active on
                        force — saying "it will be cancelled too" here would be
                        false and fiscally misleading. */}
                    Η παραγγελία έχει παραστατικό. Με την ακύρωση από τα κεντρικά το παραστατικό <b>ΠΑΡΑΜΕΝΕΙ ενεργό</b> — αν χρειάζεται ακυρωτικό, ακύρωσέ το ξεχωριστά από τα Παραστατικά του καταστήματος.
                  </div>
                ) : fiscalCanCancel ? (
                  <div className="text-amber-900/90 dark:text-amber-200/90">
                    {t('cancelOrder.fiscalActiveBody', {
                      defaultValue: 'Η παραγγελία έχει παραστατικό. Με την ακύρωση θα ακυρωθεί και το παραστατικό — θα εκδοθεί ακυρωτικό στην ΑΑΔΕ.',
                    })}
                  </div>
                ) : (
                  <div className="text-amber-900/80 dark:text-amber-200/80 text-xs">
                    {t('cancelOrder.fiscalNoMark', {
                      defaultValue: 'Το παραστατικό δεν έχει MARK (δεν έχει διαβιβαστεί) — θα ακυρωθεί μόνο η παραγγελία.',
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Box has NO integrator cancellation flow after accept (Desquared,
              Jul 2026) — the platform keeps the order live unless contacted. */}
          {String(platformName || '').toLowerCase() === 'box' && (
            <div className="flex gap-2 p-3 rounded-lg bg-sky-100 dark:bg-sky-950/40 border border-sky-300 dark:border-sky-800 text-sky-800 dark:text-sky-200 text-xs">
              <span className="shrink-0">ℹ️</span>
              <span>Η Box <b>δεν ενημερώνεται αυτόματα</b> για την ακύρωση — επικοινώνησε με την πλατφόρμα για να μη σταλεί διανομέας/χρεωθεί η παραγγελία.</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-t3 mb-2">
              {t('cancelOrder.reasonLabel')}
            </label>
            {/* Preset reasons — translation keys are listed in i18n's cancelOrder.presets.
                Clicking one fills the textarea but stays editable so the user can add detail. */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {[...extraPresetKeys, ...BASE_PRESET_KEYS].map(k => {
                const label = t(`cancelOrder.presets.${k}`);
                const active = reason === label;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setReason(label)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      active
                        ? 'bg-red-100 dark:bg-red-900/40 border-red-600 text-red-800 dark:text-red-200'
                        : 'bg-surface-2 dark:bg-surface-2/60 border-line-strong text-t2 hover:border-line-strong'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={t('cancelOrder.reasonPlaceholder')}
              className="w-full px-3 py-2 rounded-lg bg-surface-0 border border-line-strong text-t1 text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <button
            onClick={closeModal}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-surface-2 text-t2 hover:bg-surface-3 disabled:opacity-50"
          >
            {t('common:cancel')}
          </button>
          <button
            onClick={() => handleSubmit(needsForceConfirm)}
            disabled={submitting || !!blockedMessage}
            className={`px-4 py-2 rounded-lg text-t1 font-medium disabled:opacity-50 ${
              needsForceConfirm
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {submitting
              ? t('common:saving')
              : (needsForceConfirm
                  ? (fiscalCanCancel
                      ? t('cancelOrder.confirmForceWithFiscal', { defaultValue: 'Ακύρωση παραγγελίας + παραστατικού' })
                      : t('cancelOrder.confirmForceButton', { defaultValue: 'Ακύρωση παραγγελίας' }))
                  : (confirmLabel || t('cancelOrder.confirmButton')))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelOrderModal;
